import type { StandardTextFieldProps, TextFieldProps as MuiTextFieldProps } from '@material-ui/core/TextField';
import TextField from '@material-ui/core/TextField';
import { useField } from 'formik';
import React, { useCallback } from 'react';
import { Hint } from 'v2/components/Shared/Hint';
import useMask from 'v2/hooks/useMask';

import type { FieldProps } from 'formik';

export type TextFieldProps = FieldProps &
  Omit<StandardTextFieldProps, 'ref' | 'variant'> & {
    mask?: string;
    variant?: MuiTextFieldProps['variant'];
  };

export const TextFormField = ({ field: fieldProp, form, helperText, ...props }: TextFieldProps) => {
  const [field, meta] = useField(fieldProp.name);
  const { name } = field;
  const { mask } = props;
  const value = field?.value || '';
  const errorText = meta.touched && meta.error;
  const { maskedValue, maskClean } = useMask(mask, value);

  const onChangeHandler = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      return form.setFieldValue(field.name, maskClean(e.target.value));
    },
    [form, field.name, maskClean]
  );

  const onBlurHandler = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      const v = e.target.value;

      form.setFieldTouched(field.name, true);

      if (!v) return;

      form.setFieldValue(field.name, maskClean(e.target.value));
    },
    [form, field.name, maskClean]
  );

  return (
    <Hint inputName={field.name}>
      <TextField
        fullWidth
        helperText={errorText || helperText}
        error={!!errorText}
        name={name}
        value={maskedValue}
        onChange={onChangeHandler}
        onBlur={onBlurHandler}
        variant='standard'
        {...props}
      />
    </Hint>
  );
};

export default TextFormField;
